(() => {
  'use strict';
  const number = '\\d+(?:\\.\\d+)?';
  const group = `${number}(?:\\s*(?:[x×]|[-–])\\s*${number})*`;
  const measurement = new RegExp(`(${group})\\s*(cm|mm|inches|inch|in\\b|[″"])`, 'gi');
  const hasMetric = /\d\s*(?:cm|mm)\b/i;
  const hasInches = /\d\s*(?:inches|inch|in\b|[″"])/i;
  function formatNumber(value, source, unit) {
    const cm = value * (source === 'mm' ? 0.1 : source === 'cm' ? 1 : 2.54);
    if (unit === 'cm') return cm.toFixed(1);
    const inches = cm / 2.54;
    const quarter = Math.round(inches * 4) / 4;
    const fraction = Math.round((quarter % 1) * 100);
    if ((fraction === 25 || fraction === 75) && Math.abs(cm - quarter * 2.54) <= 0.050001) {
      return quarter.toFixed(2);
    }
    return inches.toFixed(1);
  }
  function formatSize(original, unit) {
    let text = String(original).replace(/^(\s*Size\s+\d+)\s*[–—-]\s*/i, '$1: ');
    if (hasMetric.test(text)) {
      text = text.replace(/\s*\(([^()]*)\)/g, (whole, inside) => hasInches.test(inside) && !hasMetric.test(inside) ? '' : whole);
    }
    text = text.replace(measurement, (_, dimensions, source) => {
      return dimensions.replace(/\d+(?:\.\d+)?/g, value => formatNumber(Number(value), source.toLowerCase(), unit))
        .replace(/\s*[x×]\s*/g, ' × ').replace(/\s*[-–]\s*/g, '–') + (unit === 'cm' ? ' cm' : ' in');
    }).replace(/\s+/g, ' ').trim();
    const duplicate = new RegExp(`(${group}\\s*(?:cm|in))\\s*\\(\\1\\)`, 'gi');
    return text.replace(duplicate, '$1');
  }
  globalThis.MMSizeUnits = { formatSize };
  if (typeof document === 'undefined') return;
  const config = document.getElementById('mm-size-units-config');
  const labels = config ? JSON.parse(config.textContent) : { label: 'Units', cm: 'CM', inches: 'Inches' };
  let unit = 'cm';
  try { if (localStorage.getItem('mm-size-unit') === 'in') unit = 'in'; } catch (_) {}
  const originals = new WeakMap();
  let scheduled = false;
  function dimensionValue(value, field) {
    if (hasMetric.test(value) || hasInches.test(value)) return value;
    const picker = field.closest('variant-selects');
    const productId = picker?.dataset.productId;
    if (!productId || !/^Size\s+\d+$/i.test(value)) return value;
    const style = picker.querySelector('select[name="options[Style]"]')?.value || picker.querySelector('input[name^="Style-"]:checked')?.value;
    for (const description of document.querySelectorAll('[data-mm-dimensions]')) {
      if (description.dataset.mmProductId !== productId) continue;
      for (const row of description.querySelectorAll('tr')) {
        const cells = row.querySelectorAll('td');
        if (cells.length !== 2) continue;
        const key = cells[0].textContent.trim();
        if (key !== (style ? `${style} / ${value}` : value)) continue;
        const dimensions = cells[1].dataset.mmOriginalDimensions || cells[1].textContent.trim();
        if (hasMetric.test(dimensions) || hasInches.test(dimensions)) return `${value}: ${dimensions}`;
      }
    }
    return value;
  }
  function updateText(node, original) {
    const next = formatSize(original, unit);
    if (node.textContent !== next) node.textContent = next;
  }
  function updateTextNode(node) {
    const previous = originals.get(node);
    const original = previous && node.nodeValue === previous.rendered ? previous.original : node.nodeValue;
    const rendered = formatSize(original, unit);
    originals.set(node, { original, rendered });
    if (node.nodeValue !== rendered) node.nodeValue = rendered;
  }
  function makeControl(field) {
    let control = field.previousElementSibling;
    if (!control?.classList.contains('mm-size-units')) {
      control = document.createElement('div');
      control.className = 'mm-size-units';
      control.setAttribute('role', 'group');
      control.setAttribute('aria-label', labels.label);
      const caption = document.createElement('span');
      caption.className = 'mm-size-units__label';
      caption.textContent = labels.label;
      control.append(caption);
      for (const [value, text] of [['cm', labels.cm], ['in', labels.inches]]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.mmUnit = value;
        button.textContent = text;
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          unit = value;
          try { localStorage.setItem('mm-size-unit', unit); } catch (_) {}
          refresh();
        });
        control.append(button);
      }
      field.before(control);
    }
    for (const button of control.querySelectorAll('button')) button.setAttribute('aria-pressed', String(button.dataset.mmUnit === unit));
  }
  function refresh() {
    scheduled = false;
    observer.disconnect();
    const fields = new Set();
    for (const input of document.querySelectorAll('select[name="options[Size]"], input[type="radio"][name^="Size-"]')) {
      const field = input.closest('.product-form__input, .variant-option, fieldset');
      if (field) fields.add(field);
    }
    for (const field of fields) {
      const select = field.querySelector('select[name="options[Size]"]');
      const radios = [...field.querySelectorAll('input[type="radio"][name^="Size-"]')];
      const values = (select ? [...select.options].map(option => option.value) : radios.map(input => input.value)).map(value => dimensionValue(value, field));
      if (!values.some(value => hasMetric.test(value) || hasInches.test(value))) continue;
      field.classList.add('mm-size-field');
      makeControl(field);
      if (select) {
        for (const option of select.options) {
          if (!option.hasAttribute('data-mm-original-label')) option.dataset.mmOriginalLabel = option.textContent;
          updateText(option, dimensionValue(option.dataset.mmOriginalLabel.trim(), field));
        }
      }
      for (const input of radios) {
        const label = [...field.querySelectorAll('label[for]')].find(label => label.htmlFor === input.id);
        if (!label) continue;
        for (const node of label.childNodes) if (node.nodeType === 3 && node.nodeValue.trim()) updateTextNode(node);
      }
      const selected = select ? select.value : radios.find(input => input.checked)?.value;
      if (selected) for (const summary of field.querySelectorAll('[data-selected-value]')) updateText(summary, dimensionValue(selected, field));
    }
    for (const item of document.querySelectorAll('.cart-item .product-option, .cart-item__details .product-option, [data-mm-size-display]')) {
      for (const node of item.childNodes) if (node.nodeType === 3 && /\bSize\s*\d/i.test(node.nodeValue)) updateTextNode(node);
    }
    for (const description of document.querySelectorAll('[data-mm-dimensions]')) {
      for (const cell of description.querySelectorAll('td')) {
        if (!cell.dataset.mmOriginalDimensions) cell.dataset.mmOriginalDimensions = cell.textContent.trim();
      }
      const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement.closest('script, style, .mm-size-units')) continue;
        if (hasMetric.test(node.nodeValue) || hasInches.test(node.nodeValue)) nodes.push(node);
      }
      if (nodes.length && !document.querySelector('.mm-size-field')) makeControl(description);
      for (const node of nodes) updateTextNode(node);
    }
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(refresh);
  }
  const observer = new MutationObserver(schedule);
  document.addEventListener('change', schedule, true);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('storage', event => {
    if (event.key === 'mm-size-unit') { unit = event.newValue === 'in' ? 'in' : 'cm'; refresh(); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
